/**
 * Where the world outside the house sits, and what its skyline looks like.
 *
 * The whole point of this module is that the exterior is **one place, fixed in world
 * space**. A conventional skybox is re-centred on the camera every frame, which is what
 * makes it read as infinitely far away — and it is exactly wrong here, because a backdrop
 * that follows the player has no parallax against the window frame at all. Walking past a
 * window would slide the world along with it, which is the flat-texture problem this
 * replaces, bought at a higher price.
 *
 * So the numbers below place the world once, around the house, and never move it. The
 * player walking through rooms then genuinely changes where they stand relative to it, and
 * a window frames a different part of it. Nothing simulates that; it is what happens when
 * you put something far away and look at it through a hole.
 *
 * Pure and headless like the rest of `src/game/`: this returns numbers and `src/engine`
 * turns them into a sphere and two cylinders.
 */

import { mulberry32, seedFromString } from '../../utils/seededRandom';
import { levelFootprint, type BuiltLevel } from './buildGeometry';
import type { GroundPoint } from './types';

/**
 * The camera's far plane, duplicated from `src/engine/constants.ts`.
 *
 * `src/game/` must not depend on `src/engine/` — the rules are meant to outlive the
 * renderer, and importing its constants would invert that. The copy is kept honest by a
 * unit test asserting the two are equal, which is the cheapest way to make a duplicated
 * constant safe.
 */
export const VIEW_DISTANCE_BUDGET = 100;

/**
 * How far out the backdrop goes, as a multiple of the radius enclosing the house.
 *
 * This is the one number that decides whether the effect works, and it has a real tension
 * in it. Too small and the woods read as a painted wall a short way outside, with the
 * treeline swinging like a drum as the player crosses a room. Too large and the re-framing
 * stops being perceptible and we are back to a poster on the glass. At this value a player
 * crossing the example house shifts their view of the horizon by roughly 10° — enough to
 * read as distance, not enough to look like it is rotating.
 */
const BACKDROP_SPREAD = 4.5;

/**
 * The floor under that, for a house too small to scale from. A single-room level would
 * otherwise get a horizon a few metres past its walls.
 */
export const MIN_BACKDROP_RADIUS = 24;

/**
 * Slack between the far side of the backdrop and the camera's far plane. The sphere is
 * centred on the *house*, not the eye, so a player standing at the edge of the footprint
 * is a further `houseRadius` from its far side than the centre is — get this wrong and the
 * sky is clipped away in a great circle as the player walks toward a wall.
 */
const FAR_PLANE_MARGIN = 6;

/**
 * How far outside the house the two rain shells hang. Indoors only — a grounds level's
 * night is dry (design D9), and `shells` is null there.
 *
 * The near one hangs as close to the walls as it safely can, and that is the point of it:
 * at any real distance a shell wrapping the whole house is mid-distance from every window
 * at once, so the storm has no near field and every drop is the same size. Close in, drops
 * outside the glass are large and sweep fast when the player moves, which is what makes the
 * far curtain read as far.
 *
 * The floor on it is the house's own circumradius — `houseRadius` is the enclosing circle,
 * so a gap measured from it clears the *corners*, not just the middle of a wall. On the
 * example house that leaves rain about 2.6 m outside a window and 0.9 m off the corners.
 */
const NEAR_SHELL_GAP = 0.9;
const FAR_SHELL_GAP = 13;

/** Rain must stay inside the backdrop, however the clamps above fall out. */
const SHELL_CEILING = 0.85;

/**
 * Where the near woods stand, as a fraction of the backdrop radius.
 *
 * The rest of the forest is painted on the backdrop itself, where its rows recede by tone
 * and scale but cannot slide against each other — they are all the same distance away. This
 * one row is pulled off that canvas and hung on its own sphere, close enough to the house
 * that crossing a room moves it visibly against the ridge behind it. At this fraction the
 * near trees shift about two-thirds again as far as the far ones do, which is the
 * difference between a painted backdrop and a wood you are standing outside of.
 */
const NEAR_WOODS_FRACTION = 0.55;
/** Clear of the near rain by this much, so drops never intersect a trunk. */
const NEAR_WOODS_CLEARANCE = 1.2;

/**
 * Outdoors, the same two distances again — but the level is now the *grounds*, not a
 * building, and the player is standing in the middle of them rather than looking out
 * through a hole in a wall. Both numbers are fractions of the backdrop radius.
 *
 * What changes the arithmetic is that nothing hangs between the player and the horizon any
 * more. Indoors the near woods are floored off the rain and the whole stack is squeezed
 * inside `SHELL_CEILING`; with the rain gone (design D9) the only ceiling left is the
 * backdrop itself, and the ground between the far bank and the sky is free to be spent on
 * depth. So it is: the house sits three quarters of the way out, and the painted woods
 * stand behind *it* rather than in front of it, which is the layering the outdoor
 * visual-style requirement asks for — bank, then house, then woods, then sky.
 *
 * The house fraction is what decides how big the building reads. On the shipped 12×12 maze
 * these put its near wall about 21 m past the bank the player stands on, where it subtends
 * roughly 25° — a house across the water. At the old rain-derived band it was 11 m out and
 * nearly half the view, which read as a wall on the far side of a ditch.
 */
const GROUNDS_HOUSE_FRACTION = 0.75;
const GROUNDS_WOODS_FRACTION = 0.95;

export type ExteriorFit = {
  /** Middle of the house on the ground plane — the world is centred here and stays. */
  center: GroundPoint;
  /** Radius of a circle enclosing the house, corners included. */
  houseRadius: number;
  backdropRadius: number;
  /**
   * How far out the scenery on the far bank stands, measured to its near face — or null
   * for a level of rooms, which has no far bank because the player is inside the thing
   * everything else is arranged around.
   */
  houseBandRadius: number | null;
  /**
   * Where the nearest row of trees stands, on its own sphere inside the backdrop. This is
   * the one part of the woods with real parallax rather than painted perspective.
   */
  nearWoodsRadius: number;
  /**
   * Rain shell radii, near first — two distances is what makes rain read as depth — or
   * null where it is not raining at all. Outdoors it never is.
   */
  shells: readonly [number, number] | null;
};

/** One tree on the skyline, in normalised 0..1 texture space around the horizon. */
export type TreeSpike = { at: number; halfWidth: number; height: number };

const TREE_COUNT = 140;

/**
 * A tree standing outside the house as actual geometry, positioned relative to the
 * exterior's centre.
 *
 * These are the only things out there with volume. Everything else — backdrop, near woods,
 * rain — is a painted surface at a fixed distance, so it moves as one piece. A handful of
 * real trunks a few metres beyond the wall move against *each other* and against the woods
 * behind them, which is the cue that says there is space out there rather than a picture.
 */
export type OutsideTree = {
  /** Offset from the exterior's centre on the ground plane. */
  x: number;
  z: number;
  /** Trunk base to tip. */
  height: number;
  /** Half-width of the foliage cone at its base. */
  spread: number;
  /** A few degrees off vertical, so a stand of them does not look planted in a grid. */
  lean: number;
  /** Yaw, so two trees of the same size do not present the same facets to the window. */
  spin: number;
};

const OUTSIDE_TREE_COUNT = 7;
/** Nearest and furthest a standing tree may be from the house's own enclosing circle. */
const TREE_RING_NEAR = 2;
const TREE_RING_FAR = 8;

/**
 * Outdoors those same trees stand on the far shore instead, as fractions of the near
 * woods' own radius: the front rank of that painted row, a stride or two in front of it.
 *
 * Indoors the ring is measured off the *house*, because a handful of trunks a few metres
 * outside the window is the whole point of them — they are what has parallax against the
 * woods. Outdoors there is no window to stand near and the ring would land in the moat, so
 * they join the treeline instead, where they give its silhouette some volume and keep the
 * far bank from being a single flat curtain.
 */
const FAR_SHORE_RING_NEAR = 0.94;
const FAR_SHORE_RING_FAR = 0.99;

export function buildExterior(built: BuiltLevel): ExteriorFit {
  const { center, radius: houseRadius } = levelFootprint(built);
  // Not "is there a maze": is the player *outdoors*, standing in the middle of the world
  // instead of looking out at it. That is what changes every distance below.
  const outdoors = built.grounds !== null;

  // Order matters: the floor is a preference, the far plane is a hard limit. A horizon a
  // little too close is a look problem; a horizon sliced through by the camera is a bug.
  const wanted = Math.max(houseRadius * BACKDROP_SPREAD, MIN_BACKDROP_RADIUS);
  const backdropRadius = Math.min(wanted, VIEW_DISTANCE_BUDGET - houseRadius - FAR_PLANE_MARGIN);

  const shell = (gap: number) =>
    Math.min(houseRadius + gap, backdropRadius * SHELL_CEILING);
  const shells: readonly [number, number] | null = outdoors
    ? null
    : [shell(NEAR_SHELL_GAP), shell(FAR_SHELL_GAP)];

  // Indoors: between the near rain and the backdrop — drops in front of the trunks, ridge
  // behind them. Floored off the near shell so a small house cannot end up with its woods
  // inside its own rain. Outdoors there is no rain to clear and the woods go to the back of
  // the stack instead, behind the house on the far bank.
  const nearWoodsRadius = shells
    ? Math.min(
        Math.max(backdropRadius * NEAR_WOODS_FRACTION, shells[0] * NEAR_WOODS_CLEARANCE),
        backdropRadius * SHELL_CEILING,
      )
    : backdropRadius * GROUNDS_WOODS_FRACTION;

  return {
    center,
    houseRadius,
    backdropRadius,
    houseBandRadius: outdoors ? backdropRadius * GROUNDS_HOUSE_FRACTION : null,
    nearWoodsRadius,
    shells,
  };
}

/**
 * Where the standing trees go: a loose ring around the house, seeded so a profile grows
 * the same ones every build.
 *
 * Each takes its own slice of the circle rather than a free angle, because unconstrained
 * random placement clumps — and a clump on one side leaves the windows on the other side
 * looking out at nothing. The jitter within a slice is what keeps it from reading as a
 * planted row.
 */
export function outsideTrees(seed: string, fit: ExteriorFit): OutsideTree[] {
  const random = mulberry32(seedFromString(`outside-trees:${seed}`));
  const trees: OutsideTree[] = [];
  const slice = (Math.PI * 2) / OUTSIDE_TREE_COUNT;

  for (let tree = 0; tree < OUTSIDE_TREE_COUNT; tree++) {
    const angle = slice * (tree + 0.2 + random() * 0.6);
    // One draw from the sequence either way, so the indoor ring is unchanged tree for tree.
    const radius =
      fit.houseBandRadius === null
        ? fit.houseRadius + TREE_RING_NEAR + random() * (TREE_RING_FAR - TREE_RING_NEAR)
        : fit.nearWoodsRadius *
          (FAR_SHORE_RING_NEAR + random() * (FAR_SHORE_RING_FAR - FAR_SHORE_RING_NEAR));

    trees.push({
      x: Math.cos(angle) * radius,
      z: Math.sin(angle) * radius,
      height: 4.5 + random() * 4,
      spread: 0.9 + random() * 0.7,
      lean: (random() - 0.5) * 0.09,
      spin: random() * Math.PI * 2,
    });
  }

  return trees;
}

/**
 * The skyline, as spikes around the horizon.
 *
 * Kept separate from the drawing for the same reason `rainStreaks` is: the horizon is a
 * loop, so a tree straddling the seam has to be drawn on both sides of it. Miss that and
 * the woods have a notch in them, always behind the same window.
 */
export function treeline(seed: string, count = TREE_COUNT): TreeSpike[] {
  const random = mulberry32(seedFromString(`treeline:${seed}`));
  const spikes: TreeSpike[] = [];

  for (let tree = 0; tree < count; tree++) {
    const spike: TreeSpike = {
      at: random(),
      halfWidth: 0.004 + random() * 0.01,
      height: 0.3 + random() * 0.7,
    };
    spikes.push(spike);
    if (spike.at + spike.halfWidth > 1) spikes.push({ ...spike, at: spike.at - 1 });
    if (spike.at - spike.halfWidth < 0) spikes.push({ ...spike, at: spike.at + 1 });
  }

  return spikes;
}
